'use client';

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, getDay, isValid, nextDay, parse, isBefore, startOfDay, type Day } from 'date-fns';
import { useSearchParams } from 'next/navigation';
import { URLParameters } from '@/app/common/urlParameters';
import { updateUrl } from '@/app/common/urlParametersUtils';
import { canPublishResolvedBusinessProfile } from '@/app/common/businessProfile';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants, getTotalNumRooms } from '@/utils/peopleUtils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { SubSourceType } from '@/app/common/starTrack/events/searchRequested';
import { InitialParamsContext } from './InitialParamsProvider';
import { changeDateFormat, getNights, SERVICES_DATES_FORMAT } from './common/dates';
import { mealPlanClassMap } from './common/consts';

type DatesOrigin = 'INITIAL' | 'PRICE_CALENDAR' | 'ALTERNATIVE_DATES' | 'DATES_PICKER' | 'APEX_CALENDAR';
type NewDates = {
  checkin: string;
  checkout: string;
  nights: number;
  origin: DatesOrigin;
};

export type UrlSearchParamsContextProps = {
  accommodationOnly: boolean;
  adults: number;
  checkin: string;
  checkout: string;
  children: number;
  datesOrigin: DatesOrigin;
  // Bumped on every setDates call, even when the picked stay is the one already set: consumers can tell a
  // re-pick of the current stay apart from no pick at all (see OffersProvider). (MPH-1140)
  datesSetCount: number;
  errorType: CheckoutErrorType | undefined;
  freeCancellationFilter: boolean;
  infants: number;
  isPastCheckin: boolean;
  isRefreshingDates: boolean;
  isTrain: boolean;
  koProviderId?: string;
  mealPlanFilterValues: string[];
  nights: number;
  totalGuests: number;
  totalGuestsWithoutInfants: number;
  totalRooms: number;
  travellerGroups: TravellerGroup[];
  setErrorType: React.Dispatch<React.SetStateAction<CheckoutErrorType | undefined>>;
  setDates: (params: NewDates) => void;
};

export const UrlSearchParamsContext = createContext<UrlSearchParamsContextProps>({
  accommodationOnly: false,
  adults: 0,
  checkin: '',
  checkout: '',
  children: 0,
  datesOrigin: 'INITIAL',
  datesSetCount: 0,
  errorType: undefined,
  freeCancellationFilter: false,
  infants: 0,
  isTrain: false,
  isPastCheckin: false,
  isRefreshingDates: false,
  koProviderId: undefined,
  mealPlanFilterValues: [],
  nights: 0,
  totalGuests: 0,
  totalGuestsWithoutInfants: 0,
  totalRooms: 0,
  travellerGroups: [],
  setErrorType: () => {},
  setDates: () => {},
});

export function UrlSearchParamsProvider(props: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const { searchInfoData, localization } = usePageWrapperData();
  const { handleRefreshSearchFromHotelDetails } = useCommonClient();
  // Read the context directly instead of through useInitialParams: this provider is also mounted
  // without an InitialParamsProvider above it (the date-picker stories), where the hook throws.
  const resetPriceContext = React.useContext(InitialParamsContext)?.resetPriceContext;

  const departureIntervals = searchParams.get(URLParameters.TRIP_DATES);
  const leadTime = searchParams.get(URLParameters.TRIP_DATES_LEAD_TIME);
  const lengthOfStay = searchParams.get(URLParameters.TRIP_DATES_LENGTH_OF_STAY);
  const checkInOnDay = searchParams.get(URLParameters.TRIP_DATES_CHECKIN_ON_DAY);
  const adultsParam = searchParams.get(URLParameters.ADULTS);
  const agesParam = searchParams.get(URLParameters.AGES);
  // MOVED TO useEffect due to error when showing error messages
  // const errorType = (searchParams.get(URLParameters.CHECKOUT_ERROR_TYPE) as CheckoutErrorType) || undefined;
  const [errorType, setErrorType] = useState<CheckoutErrorType | undefined>(undefined);
  const koProviderId = searchParams.get(URLParameters.KOPROVIDER_ID) || undefined;
  const searchMode = searchParams.get(URLParameters.SEARCH_MODE) || searchInfoData.searchMode;
  const mealPlanFilterParam = searchParams.get(URLParameters.MEAL);
  const oldMealPlanFilterParam = searchParams.get(URLParameters.MEALPLAN);
  const freeCancellationFilterParam = searchParams.get(URLParameters.FREE_CANCELLATION);

  const urlDates = getDatesFromQueryParams({ departureIntervals, leadTime, lengthOfStay, checkInOnDay });
  const checkin = urlDates[0];
  const checkout = urlDates[1];
  const nights = getNights(checkin, checkout);
  const adults = getNumberOfAdults(adultsParam || '');
  const children = getNumberOfChildren(agesParam || '');
  const infants = getNumberOfInfants(agesParam || '');
  const totalGuests = adults + children + infants;
  const totalGuestsWithoutInfants = adults + children;
  const totalRooms = getNumberOfRooms(adultsParam || '');
  const travellerGroups = getTravellerGroups(adultsParam || '', agesParam || '');
  const accommodationOnly = searchMode === 'HO';
  const isTrain = searchMode === 'TH';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mealPlanFilterValues = mealPlanFilterParam
    ? mealPlanFilterParam
        .split(',')
        .map((value) => mealPlanClassMap[value])
        .filter((value) => value)
    : oldMealPlanFilterParam
      ? oldMealPlanFilterParam.split(',').filter((value) => value)
      : [];
  const freeCancellationFilter = freeCancellationFilterParam === '1';

  const [dates, setDates] = useState<NewDates>({ checkin, checkout, nights, origin: 'INITIAL' });

  const isPastCheckin = isBefore(parse(dates.checkin, SERVICES_DATES_FORMAT, new Date()), startOfDay(new Date()));
  const [isRefreshingDates, setIsRefreshingDates] = useState(false);
  const [datesSetCount, setDatesSetCount] = useState(0);

  const setNewDates = useCallback(async (params: NewDates) => {
    setIsRefreshingDates(true);
    try {
      // A rate context belongs to one stay, so it must not price a different one — the pricing request
      // normally consumes it, but on EVO it is deferred behind the apex calendar and a date change can
      // beat it. The apex calendar seeds the picked day's own context immediately before calling this,
      // so it is the one origin that must keep what it just set. (MPH-904)
      if (params.origin !== 'APEX_CALENDAR') resetPriceContext?.();
      setDates({
        checkin: params.checkin,
        checkout: params.checkout,
        nights: params.nights,
        origin: params.origin,
      });
      setDatesSetCount((count) => count + 1);

      await handleRefreshSearchFromHotelDetails({
        subSourceType:
          params.origin === 'PRICE_CALENDAR'
            ? SubSourceType.HotelDetailPriceCalendar
            : params.origin === 'APEX_CALENDAR'
              ? SubSourceType.HotelDetailApexCalendar
              : params.origin === 'DATES_PICKER'
                ? SubSourceType.HotelDetailDatesSelector
                : params.origin === 'ALTERNATIVE_DATES'
                  ? SubSourceType.HotelDetailAlternativeDates
                  : undefined,
        syncCSWDates: { dateFrom: params.checkin, dateTo: params.checkout },
        extraQueryParamsToSet: [
          { [URLParameters.TRIP_DATES]: `${params.checkin}-${params.checkout}` },
          { [URLParameters.DATE_FROM]: changeDateFormat(params.checkin, 'yyyyMMdd', 'yyyy-MM-dd') },
          { [URLParameters.DATE_TO]: changeDateFormat(params.checkout, 'yyyyMMdd', 'yyyy-MM-dd') },
        ],
        extraQueryParamsToRemove: [
          URLParameters.VC_SEARCH_ID,
          URLParameters.CUSTOM_CHECKIN_CHECKOUT,
          // The rate context describes the price of the stay the user arrived with, so it must not
          // survive a date change: any later read of the URL (a refresh, a shared link, back/forward)
          // would absorb that price onto the new dates. Invisible on days the apex calendar carries a
          // context for, since that context replaces the stale one, but on a day without one (sold
          // out, departure today) nothing outranks it and the old price prices a different stay. (MPH-904)
          URLParameters.RATE_PRICE_CONTEXT,
          URLParameters.RATE_PRICE_CONTEXT_SIGNATURE,
          // The apex calendar restarts the selection from the default (cheapest) offer: drop
          // the selection params so SelectedMealPlanProvider cannot re-seed from them (a stale
          // mealPlanCode would still match the new offers by code).
          ...(params.origin === 'APEX_CALENDAR'
            ? [URLParameters.ROOM_ID, URLParameters.MEALPLAN_ID, URLParameters.MEALPLAN_CODE]
            : []),
        ],
      });
    } finally {
      setIsRefreshingDates(false);
    }
  }, []);

  useEffect(() => {
    const queryParamsToSet = [];
    const queryParamsToRemove = [];

    const errorTypeParam = (searchParams.get(URLParameters.CHECKOUT_ERROR_TYPE) as CheckoutErrorType) || undefined;
    setErrorType(errorTypeParam);

    const backKoProviderId = searchParams.get(URLParameters.CHECKOUT_BACK_KOPROVIDER_ID);
    if (backKoProviderId) {
      queryParamsToRemove.push(URLParameters.CHECKOUT_BACK_KOPROVIDER_ID);
      if (errorTypeParam && ['ROOM_NOT_AVAILABLE', 'HOTEL_NOT_AVAILABLE'].includes(errorTypeParam)) {
        queryParamsToSet.push({ [URLParameters.KOPROVIDER_ID]: backKoProviderId });
      }
    }
    // MPH-960: never let the resolved profile replace a different one that arrived in the URL — see
    // canPublishResolvedBusinessProfile. The results page guards the same overwrite in enforceSearchId.
    if (
      canPublishResolvedBusinessProfile({
        resolved: localization.businessProfile,
        fromUrl: searchParams.get(URLParameters.BUSINESS_PROFILE_ID),
      })
    ) {
      queryParamsToSet.push({ [URLParameters.BUSINESS_PROFILE_ID]: localization.businessProfile });
    }
    if (errorTypeParam) {
      queryParamsToRemove.push('errorType');
    }

    queryParamsToRemove.push('meta');

    updateUrl({ queryParamsToSet, queryParamsToRemove });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memoizedValue = useMemo(
    () => ({
      accommodationOnly,
      adults,
      checkin: dates.checkin,
      checkout: dates.checkout,
      children,
      datesOrigin: dates.origin,
      datesSetCount,
      errorType,
      freeCancellationFilter,
      infants,
      isTrain,
      isRefreshingDates,
      mealPlanFilterValues,
      nights: dates.nights,
      koProviderId,
      totalGuests,
      totalRooms,
      totalGuestsWithoutInfants,
      travellerGroups,
      isPastCheckin,
      setErrorType,
      setDates: setNewDates,
    }),
    [
      accommodationOnly,
      adults,
      dates.checkin,
      dates.checkout,
      children,
      dates.origin,
      datesSetCount,
      errorType,
      freeCancellationFilter,
      infants,
      isTrain,
      isRefreshingDates,
      mealPlanFilterValues,
      koProviderId,
      dates.nights,
      totalGuests,
      totalGuestsWithoutInfants,
      totalRooms,
      travellerGroups,
      isPastCheckin,
      setErrorType,
      setNewDates,
    ]
  );

  return <UrlSearchParamsContext.Provider value={memoizedValue}>{props.children}</UrlSearchParamsContext.Provider>;
}

export function useUrlSearchParams() {
  const context = React.useContext(UrlSearchParamsContext);

  if (context === undefined) {
    throw new Error('useUrlSearchParams must be within a UrlSearchParamsProvider');
  }

  return context;
}

function getDatesFromQueryParams(queryParams: {
  checkInOnDay: string | null;
  departureIntervals: string | null;
  leadTime: string | null;
  lengthOfStay: string | null;
}) {
  const { departureIntervals, leadTime, lengthOfStay, checkInOnDay } = queryParams;

  const dates =
    departureIntervals && isValidDepartureIntervals(departureIntervals)
      ? departureIntervals.split('-')
      : leadTime && lengthOfStay
        ? datesFromLeadTimeLengthOfStay(Number(leadTime), Number(lengthOfStay), checkInOnDay?.toLowerCase())
        : getDefaultDates();

  return dates;
}

function isValidDepartureIntervals(departureIntervals: string) {
  const [checkin, checkout] = departureIntervals.split('-');
  return isValidDate(checkin) && isValidDate(checkout);
}

function isValidDate(date: string, dateFormat = 'yyyyMMdd') {
  const parsedDate = parse(date, dateFormat, new Date());
  return isValid(parsedDate);
}

function datesFromLeadTimeLengthOfStay(leadTime: number, lengthOfStay: number, checkInOnDay?: string) {
  if (Number.isNaN(leadTime) || Number.isNaN(lengthOfStay)) {
    return [addDaysToTodayFormatted(60), addDaysToTodayFormatted(67)];
  }

  if (checkInOnDay) {
    const nextWeekdayDate = leadTimeNextWeekdayDates(leadTime, checkInOnDay);
    return [format(nextWeekdayDate, 'yyyyMMdd'), format(addDays(nextWeekdayDate, lengthOfStay), 'yyyyMMdd')];
  }
  return [addDaysToTodayFormatted(leadTime), addDaysToTodayFormatted(leadTime + lengthOfStay)];
}

function getDefaultDates() {
  return [addDaysToTodayFormatted(7), addDaysToTodayFormatted(9)];
}

function addDaysToTodayFormatted(days: number) {
  return format(addDaysToToday(days), 'yyyyMMdd');
}

function addDaysToToday(days: number) {
  const today = new Date();
  today.setDate(today.getDate() + days);
  return today;
}

function leadTimeNextWeekdayDates(leadTime: number, dayOfTheWeek: string) {
  const leadTimeDate = addDaysToToday(leadTime);

  const isLeadTimeSameDayOfTheWeek = format(new Date(), 'EEEE').toLowerCase() === dayOfTheWeek.toLocaleLowerCase();
  const leadTimeDayOfTheWeek = parse(dayOfTheWeek.toLowerCase(), 'cccc', leadTimeDate);

  return isLeadTimeSameDayOfTheWeek || !isValid(leadTimeDayOfTheWeek)
    ? leadTimeDate
    : nextDay(leadTimeDate, getDay(parse(dayOfTheWeek.toLowerCase(), 'cccc', leadTimeDate)) as Day);
}

function getNumberOfChildren(agesParam: string): number {
  return getTotalNumChildren(agesParam);
}

function getNumberOfInfants(agesParam: string): number {
  return getTotalNumInfants(agesParam);
}

function getNumberOfAdults(adultsParam: string): number {
  return getTotalNumAdults(adultsParam);
}

function getNumberOfRooms(adultsParam: string): number {
  return getTotalNumRooms(adultsParam);
}

function getTravellerGroups(adultsParam: string, agesParam: string): TravellerGroup[] {
  const travellerGroups: TravellerGroup[] = adultsParam.split(';').map((adultsByRoom) => ({
    adults: Number(adultsByRoom),
    childrenAges: [],
  }));

  agesParam.split(';').forEach((childrenAgesByRoom, i) => {
    if (childrenAgesByRoom.trim() !== '') {
      const children = childrenAgesByRoom.split(',');
      travellerGroups[i].childrenAges = children.map((childAge) => Number(childAge));
    }
  });

  return travellerGroups;
}

export function updateUrlTransportId(transportId: string) {
  updateUrl({
    queryParamsToSet: [{ [URLParameters.TRANSPORT_ID]: transportId }],
  });
}
